import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { CaseFile, RequiredFile } from '../../types';

interface ManualFileMatchModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: (file_id: string, requirement_id: string, month?: string) => Promise<void>;
  file: CaseFile | null;
  requirements: RequiredFile[];
}

const ManualFileMatchModal: React.FC<ManualFileMatchModalProps> = ({ 
  show, 
  onHide, 
  onSubmit,
  file,
  requirements 
}) => {
  const [selectedRequirementId, setSelectedRequirementId] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      setSelectedRequirementId('');
      setSelectedMonth('');
      setIsSubmitting(false);
    }
  }, [show]);

  const selectedRequirement = requirements.find(r => r.requirement_id === selectedRequirementId);
  const isBankStatement = selectedRequirement?.file_type === 'bank_statement';
  const pendingMonths = selectedRequirement?.required_months?.filter(m => m.status === 'pending') || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !selectedRequirementId) return;
    if (isBankStatement && !selectedMonth) {
      alert('Please select a month to satisfy.');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(file.file_id, selectedRequirementId, isBankStatement ? selectedMonth : undefined);
      onHide();
    } catch (error) {
      console.error('Error matching file:', error);
      alert('Failed to match file. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter requirements that could potentially match this file
  const getCompatibleRequirements = (): RequiredFile[] => {
    if (!file) return requirements;
    
    // For now, show all requirements - user can decide
    // In the future, we could filter by file_type compatibility
    return requirements;
  };

  const compatibleRequirements = getCompatibleRequirements();

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Manually Match File to Requirement</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {file && (
            <div className="mb-3">
              <strong>File:</strong> {file.filename}
              <br />
              <small className="text-muted">Type: {file.file_type}</small>
            </div>
          )}
          
          <Form.Group className="mb-3">
            <Form.Label>Select Requirement <span className="text-danger">*</span></Form.Label>
            <Form.Select
              required
              value={selectedRequirementId}
              onChange={(e) => {
                setSelectedRequirementId(e.target.value);
                setSelectedMonth(''); // Reset month when requirement changes
              }}
            >
              <option value="">Choose a requirement...</option>
              {compatibleRequirements.map(req => (
                <option key={req.requirement_id} value={req.requirement_id}>
                  {req.description} ({req.category} - {req.file_type})
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Select which requirement this file should satisfy
            </Form.Text>
          </Form.Group>

          {isBankStatement && pendingMonths.length > 0 && (
            <Form.Group className="mb-3">
              <Form.Label>Select Month to Satisfy <span className="text-danger">*</span></Form.Label>
              <Form.Select
                required
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                <option value="">Choose a month...</option>
                {pendingMonths.map(month => (
                  <option key={month.month} value={month.month}>
                    {month.month}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Select which month this bank statement satisfies
              </Form.Text>
            </Form.Group>
          )}

          {isBankStatement && pendingMonths.length === 0 && (
            <div className="alert alert-info">
              All months for this requirement are already satisfied.
            </div>
          )}

          {compatibleRequirements.length === 0 && (
            <div className="alert alert-warning">
              No requirements available for this case.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit" 
            disabled={!selectedRequirementId || isSubmitting || (isBankStatement && !selectedMonth)}
          >
            {isSubmitting ? 'Matching...' : 'Match File'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ManualFileMatchModal;

